import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Truck, Package, Route, Clock, TrendingUp, Building2, BarChart3 } from 'lucide-react';
import Page from '../../components/Page';
import { KPICard, InfoCard } from '../../components/cards';

const LogisticsOverviewPage: React.FC = () => {
  // Dados simulados para demonstração - em produção viriam do backend
  const logisticsData = {
    totalRoutes: 156,
    activeVehicles: 89,
    totalPackages: 12450,
    averageDeliveryTime: 2.4,
    efficiency: 94.2,
    coverage: 78.5,
    totalClients: 234,
    distributionCenters: 12
  };

  const recentActivities = [
    { id: 1, action: 'Nova rota otimizada', location: 'São Paulo → Rio de Janeiro', time: '2h atrás', status: 'success' },
    { id: 2, action: 'Veículo em manutenção', location: 'Centro de Distribuição SP', time: '4h atrás', status: 'warning' },
    { id: 3, action: 'Entrega concluída', location: 'Cliente ABC Corp', time: '6h atrás', status: 'success' },
    { id: 4, action: 'Rota alterada', location: 'Belo Horizonte → Brasília', time: '8h atrás', status: 'info' }
  ];

  const topRoutes = [
    { route: 'São Paulo → Rio de Janeiro', packages: 1250, efficiency: 98.5 },
    { route: 'São Paulo → Belo Horizonte', packages: 980, efficiency: 96.2 },
    { route: 'Rio de Janeiro → Brasília', packages: 750, efficiency: 94.8 },
    { route: 'São Paulo → Curitiba', packages: 680, efficiency: 97.1 },
    { route: 'Belo Horizonte → Salvador', packages: 520, efficiency: 93.5 }
  ];

  return (
    <Page
      title="🗺️ Malha Logística - Visão Geral"
      subtitle="Dashboard principal da Malha Logística"
      onRefresh={() => window.location.reload()}
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="space-y-8"
      >
        {/* KPIs Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { 
              key: 'totalRoutes', 
              label: 'Rotas Ativas', 
              value: logisticsData.totalRoutes.toString(), 
              icon: Route, 
              color: 'blue' as const,
              change: { value: '+12', type: 'increase' as const }
            },
            { 
              key: 'activeVehicles', 
              label: 'Veículos Ativos', 
              value: logisticsData.activeVehicles.toString(), 
              icon: Truck, 
              color: 'green' as const,
              change: { value: '+5', type: 'increase' as const }
            },
            { 
              key: 'totalPackages', 
              label: 'Pacotes Hoje', 
              value: logisticsData.totalPackages.toLocaleString('pt-BR'), 
              icon: Package, 
              color: 'purple' as const,
              change: { value: '+8.2%', type: 'increase' as const }
            },
            { 
              key: 'efficiency', 
              label: 'Eficiência Geral', 
              value: `${logisticsData.efficiency}%`, 
              icon: TrendingUp, 
              color: 'orange' as const,
              change: { value: '+2.1%', type: 'increase' as const }
            }
          ].map((kpi, index) => (
            <KPICard
              key={kpi.key}
              label={kpi.label}
              value={kpi.value}
              icon={kpi.icon}
              color={kpi.color}
              change={kpi.change}
              animation={{
                initial: { opacity: 0, y: 20 },
                animate: { opacity: 1, y: 0 },
                transition: { delay: 0.3 + index * 0.1 },
                whileHover: { y: -5, scale: 1.02 }
              }}
            />
          ))}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Recent Activities */}
          <InfoCard
            title="Atividades Recentes"
            subtitle="Últimas 24 horas"
            icon={Clock}
            animation={{
              initial: { opacity: 0, x: -20 },
              animate: { opacity: 1, x: 0 },
              transition: { delay: 0.4 }
            }}
          >
            <div className="space-y-4">
              {recentActivities.map((activity, index) => (
                <motion.div
                  key={activity.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="flex items-center justify-between p-4 rounded-xl"
                  style={{ backgroundColor: 'var(--bg-secondary)' }}
                >
                  <div className="flex items-center">
                    <div 
                      className={`w-3 h-3 rounded-full mr-3 ${
                        activity.status === 'success' ? 'bg-green-500' :
                        activity.status === 'warning' ? 'bg-yellow-500' :
                        'bg-blue-500'
                      }`}
                    />
                    <div>
                      <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                        {activity.action}
                      </p>
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {activity.location}
                      </p>
                    </div>
                  </div>
                  <span className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                    {activity.time}
                  </span>
                </motion.div>
              ))}
            </div>
          </InfoCard>

          {/* Top Routes */}
          <InfoCard
            title="Top Rotas"
            subtitle="Por volume de pacotes"
            icon={BarChart3}
            animation={{
              initial: { opacity: 0, x: 20 },
              animate: { opacity: 1, x: 0 },
              transition: { delay: 0.5 }
            }}
          >
            <div className="space-y-4">
              {topRoutes.map((route, index) => (
                <motion.div
                  key={route.route}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 + index * 0.1 }}
                  className="p-4 rounded-xl"
                  style={{ backgroundColor: 'var(--bg-secondary)' }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium" style={{ color: 'var(--text-primary)' }}>
                      {route.route}
                    </h4>
                    <span className="text-sm font-bold" style={{ color: 'var(--primary)' }}>
                      {route.efficiency}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                      {route.packages.toLocaleString('pt-BR')} pacotes
                    </span>
                    <div className="w-24 bg-gray-200 rounded-full h-2">
                      <div 
                        className="h-2 rounded-full transition-all duration-500"
                        style={{ 
                          width: `${route.efficiency}%`,
                          backgroundColor: 'var(--primary)'
                        }}
                      />
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </InfoCard>
        </div>

        {/* Additional Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <InfoCard
            title="Tempo Médio de Entrega"
            subtitle="Em horas"
            icon={Clock}
            animation={{
              initial: { opacity: 0, y: 20 },
              animate: { opacity: 1, y: 0 },
              transition: { delay: 0.7 }
            }}
          >
            <div className="text-center">
              <p className="text-4xl font-bold mb-2" style={{ color: 'var(--primary)' }}>
                {logisticsData.averageDeliveryTime}h
              </p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Redução de 15% vs mês anterior
              </p>
            </div>
          </InfoCard>

          <InfoCard
            title="Cobertura Geográfica"
            subtitle="Estados atendidos"
            icon={MapPin}
            animation={{
              initial: { opacity: 0, y: 20 },
              animate: { opacity: 1, y: 0 },
              transition: { delay: 0.8 }
            }}
          >
            <div className="text-center">
              <p className="text-4xl font-bold mb-2" style={{ color: 'var(--primary)' }}>
                {logisticsData.coverage}%
              </p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                {logisticsData.totalClients} clientes ativos
              </p>
            </div>
          </InfoCard>

          <InfoCard
            title="Centros de Distribuição"
            subtitle="Ativos"
            icon={Building2}
            animation={{
              initial: { opacity: 0, y: 20 },
              animate: { opacity: 1, y: 0 },
              transition: { delay: 0.9 }
            }}
          >
            <div className="text-center">
              <p className="text-4xl font-bold mb-2" style={{ color: 'var(--primary)' }}>
                {logisticsData.distributionCenters}
              </p>
              <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                Capacidade total: 95%
              </p>
            </div>
          </InfoCard>
        </div>
      </motion.div>
    </Page>
  );
};

export default LogisticsOverviewPage;
